import type {
  ConjointAttributeId,
  ConjointProfile,
  InterviewItem,
  OptionId,
  QAState,
} from "../types";
import { parseConjointPriceLevel } from "./index";

type AnsweredConjointTask = {
  profiles: ConjointProfile[];
  chosenProfileIndex: number;
};

// The tasks extraction reads, so a follow-up never describes choices the fit did
// not see. A replayed task is appended, so its last submission wins.
export const getAnsweredConjointTasks = (
  statesForItem: QAState[]
): AnsweredConjointTask[] => {
  const latestByTaskIndex = new Map<number, AnsweredConjointTask>();
  for (const state of statesForItem) {
    const task = state.conjointTask;
    if (
      !task ||
      state.submittedAt === undefined ||
      task.chosenProfileIndex === undefined ||
      task.profiles.length < 2
    ) {
      continue;
    }
    latestByTaskIndex.set(task.taskIndex, {
      profiles: task.profiles,
      chosenProfileIndex: task.chosenProfileIndex,
    });
  }
  return [...latestByTaskIndex.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, task]) => task);
};

const levelIdFor = (
  profile: ConjointProfile,
  attributeId: ConjointAttributeId
): OptionId | undefined =>
  profile.levels.find((level) => level.attributeId === attributeId)?.levelId;

// A ridge logit on this respondent's screens, since pick counts can't separate
// attributes the design keeps pairing. The penalty keeps an always-winning level
// finite; 300 steps match 3000 to four decimals on 12 tasks, in about 6ms.
const RIDGE_PENALTY = 1;
const FIT_STEPS = 300;
const STEP_SIZE = 0.1;

const getRespondentPartWorths = (
  item: InterviewItem,
  tasks: AnsweredConjointTask[]
): Map<OptionId, number> | undefined => {
  const picked = tasks.flatMap((task) => {
    const chosen = task.profiles[task.chosenProfileIndex];
    return chosen
      ? [{ profiles: task.profiles, chosen: task.chosenProfileIndex }]
      : [];
  });
  // Nothing was ever picked, so no level was favored or avoided over another.
  if (picked.length === 0) return undefined;

  let nextIndex = 0;
  const levelsByAttribute = (item.attributes ?? []).map((attribute) =>
    attribute.levels.map((level) => ({ level, index: nextIndex++ }))
  );
  const levelIndex = new Map(
    levelsByAttribute
      .flat()
      .map(({ level, index }) => [level.id, index] as const)
  );
  const beta = new Array<number>(levelIndex.size).fill(0);
  const screens = picked.map(({ profiles, chosen }) => ({
    chosen,
    profiles: profiles.map((profile) =>
      profile.levels.flatMap(({ levelId }) => {
        const index = levelIndex.get(levelId);
        return index === undefined ? [] : [index];
      })
    ),
  }));
  for (let step = 0; step < FIT_STEPS; step++) {
    const gradient = beta.map((value) => -RIDGE_PENALTY * value);
    for (const screen of screens) {
      const utilities = screen.profiles.map((levels) =>
        levels.reduce((sum, index) => sum + beta[index], 0)
      );
      const max = Math.max(...utilities);
      const weights = utilities.map((utility) => Math.exp(utility - max));
      const total = weights.reduce((sum, weight) => sum + weight, 0);
      screen.profiles.forEach((levels, profileIndex) => {
        const residual =
          (profileIndex === screen.chosen ? 1 : 0) -
          weights[profileIndex] / total;
        for (const index of levels) gradient[index] += residual;
      });
    }
    gradient.forEach((value, index) => {
      beta[index] += STEP_SIZE * value;
    });
  }

  const partWorths = new Map<OptionId, number>();
  for (const levels of levelsByAttribute) {
    const mean =
      levels.reduce((sum, { index }) => sum + beta[index], 0) / levels.length;
    for (const { level, index } of levels) {
      partWorths.set(level.id, beta[index] - mean);
    }
  }
  return partWorths;
};

// Measured: an attribute that never decided a pick fits to float noise (about
// 1e-16), while a single screen it decided gives a gap near 0.7.
const NO_PREFERENCE_GAP = 1e-6;

type RankedLevel = {
  levelId: OptionId;
  text: string;
  gap: number;
};

// Ranked by the gap between the two ends, not the top level's value, since
// centering favors attributes with more levels. Price is left out because a
// cheapest pick explains itself, and the trade-off accessors cover price.
const rankFavoredLevels = (
  item: InterviewItem,
  partWorths: Map<OptionId, number>
): RankedLevel[] =>
  (item.attributes ?? [])
    .filter((attribute) => attribute.id !== item.priceAttributeId)
    .flatMap((attribute) => {
      const levels = attribute.levels.flatMap((level) => {
        const partWorth = partWorths.get(level.id);
        return partWorth === undefined ? [] : [{ level, partWorth }];
      });
      if (levels.length === 0) return [];
      // A declared order fixes the ends, so a noisy middle level can't win and a
      // backwards effect falls under the no-preference cut below.
      const [first, last] = [levels[0], levels[levels.length - 1]];
      const ordered =
        attribute.preferenceDirection === "ascending"
          ? { top: last, bottom: first }
          : attribute.preferenceDirection === "descending"
            ? { top: first, bottom: last }
            : undefined;
      // Strict comparison keeps the authored order on ties, so the same
      // respondent always yields the same text.
      const top =
        ordered?.top ??
        levels.reduce((a, b) => (b.partWorth > a.partWorth ? b : a));
      const bottom =
        ordered?.bottom ??
        levels.reduce((a, b) => (b.partWorth < a.partWorth ? b : a));
      // An attribute that never decided a pick has nothing to name.
      if (top.partWorth - bottom.partWorth < NO_PREFERENCE_GAP) return [];
      return [
        {
          levelId: top.level.id,
          // Named with its attribute, so a yes/no level still says what it is.
          text: attribute.name
            ? `${attribute.name}: ${top.level.text}`
            : top.level.text,
          gap: top.partWorth - bottom.partWorth,
        },
      ];
    })
    .sort((a, b) => b.gap - a.gap);

// "Tended to pick" claims a habit, so a level needs two picks. Below that the
// reference stays unresolved and the interviewer words around it.
const MIN_PICKS_TO_NAME_A_LEVEL = 2;

const countPicksWithLevel = (
  tasks: AnsweredConjointTask[],
  levelId: OptionId
): number =>
  tasks.filter((task) =>
    task.profiles[task.chosenProfileIndex]?.levels.some(
      (level) => level.levelId === levelId
    )
  ).length;

// Ten or so tasks only support naming one level, so this is their strongest
// pattern.
export const getFavoredConjointLevel = (
  item: InterviewItem,
  tasks: AnsweredConjointTask[]
): string | undefined => {
  const partWorths = getRespondentPartWorths(item, tasks);
  const favored = partWorths && rankFavoredLevels(item, partWorths)[0];
  return favored &&
    countPicksWithLevel(tasks, favored.levelId) >= MIN_PICKS_TO_NAME_A_LEVEL
    ? favored.text
    : undefined;
};

type ConjointTradeoff = {
  chosen: ConjointProfile | null;
  passedOver: ConjointProfile;
};

const priceOf = (
  profile: ConjointProfile,
  priceLevelValues: Map<OptionId, number>,
  priceAttributeId: ConjointAttributeId
): number | undefined => {
  const levelId = levelIdFor(profile, priceAttributeId);
  return levelId === undefined ? undefined : priceLevelValues.get(levelId);
};

// One real choice to ask about, a fact rather than an inference: the largest
// premium they paid, else the largest saving, else their closest call.
export const getConjointTradeoff = (
  item: InterviewItem,
  tasks: AnsweredConjointTask[]
): ConjointTradeoff | undefined => {
  const pairs = tasks.flatMap((task, taskOrder) => {
    const chosen = task.profiles[task.chosenProfileIndex];
    if (!chosen) return [];
    return task.profiles
      .filter((_, index) => index !== task.chosenProfileIndex)
      .map((passedOver) => ({ chosen, passedOver, taskOrder }));
  });

  if (pairs.length === 0) {
    // They picked none of these on every task, so the only fact is what they
    // passed on, taken from their last task.
    const last = tasks.at(-1);
    const passedOver = last?.profiles[0];
    return passedOver ? { chosen: null, passedOver } : undefined;
  }

  const priceAttribute = (item.attributes ?? []).find(
    (attribute) => attribute.id === item.priceAttributeId
  );
  if (priceAttribute) {
    const priceLevelValues = new Map(
      priceAttribute.levels.flatMap((level) => {
        const value = parseConjointPriceLevel(level.text);
        return value === null ? [] : [[level.id, value] as const];
      })
    );
    const priced = pairs.flatMap((pair) => {
      const chosenPrice = priceOf(
        pair.chosen,
        priceLevelValues,
        priceAttribute.id
      );
      const passedOverPrice = priceOf(
        pair.passedOver,
        priceLevelValues,
        priceAttribute.id
      );
      return chosenPrice === undefined || passedOverPrice === undefined
        ? []
        : [{ ...pair, premium: chosenPrice - passedOverPrice }];
    });
    const largest = (candidates: typeof priced) =>
      candidates.reduce<(typeof priced)[number] | undefined>(
        (best, pair) =>
          // Later tasks win ties, so the choice is the freshest in their mind.
          best === undefined ||
          Math.abs(pair.premium) > Math.abs(best.premium) ||
          (Math.abs(pair.premium) === Math.abs(best.premium) &&
            pair.taskOrder > best.taskOrder)
            ? pair
            : best,
        undefined
      );
    const paidMore = largest(priced.filter((pair) => pair.premium > 0));
    const savedMoney = largest(priced.filter((pair) => pair.premium < 0));
    const priceTradeoff = paidMore ?? savedMoney;
    if (priceTradeoff) {
      return {
        chosen: priceTradeoff.chosen,
        passedOver: priceTradeoff.passedOver,
      };
    }
  }

  // No price difference, so the closest call on their own part-worths. The gap
  // is unsigned: a pick their fit scores far below the other is noise.
  const partWorths = getRespondentPartWorths(item, tasks);
  const profileUtility = (profile: ConjointProfile) =>
    profile.levels.reduce(
      (sum, { levelId }) => sum + (partWorths?.get(levelId) ?? 0),
      0
    );
  const closest = pairs.reduce((best, pair) => {
    const margin = Math.abs(
      profileUtility(pair.chosen) - profileUtility(pair.passedOver)
    );
    const bestMargin = Math.abs(
      profileUtility(best.chosen) - profileUtility(best.passedOver)
    );
    return margin < bestMargin ||
      (margin === bestMargin && pair.taskOrder > best.taskOrder)
      ? pair
      : best;
  });
  return { chosen: closest.chosen, passedOver: closest.passedOver };
};
